import {Injectable} from '@angular/core';
import {map, shareReplay} from 'rxjs/operators';
import {ApiService} from './api.service';
import {Credentials} from '../models/credentials';
import {BehaviorSubject, firstValueFrom, Observable, Subject} from 'rxjs';
import {ApiFormat} from '../../enums/api-format';
import {AuthData, AuthStatus} from '../models/auth-status';
import {StorageService} from './storage.service';
import {Capacitor} from '@capacitor/core';
import {InAppBrowser} from '@capgo/capacitor-inappbrowser';
import {Browser} from '@capacitor/browser';
import {App} from '@capacitor/app';
import {
    classifySamlError,
    failureFromSamlCallback,
    readSamlCode,
    readSamlError,
    SamlExchangeResponse,
    SamlLoginFailure,
    SAML_REDIRECT_URI,
    isSamlCallbackUrl,
} from '../utils/saml-callback';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    authStatus = new BehaviorSubject<AuthStatus>(null);
    /**
     * Web SAML returns to `/?saml=true&email&code`. The login page exchanges it.
     */
    readonly pendingWebSamlLogin = new BehaviorSubject<{code: string; email: string} | null>(null);
    /** Native callback that arrived outside an in-flight system auth session. */
    readonly samlCallbackCode = new Subject<string>();
    readonly samlFailure = new BehaviorSubject<SamlLoginFailure | null>(null);
    limitedAccess = false;
    private clientSecret: string;
    private clientId: string;
    private samlListenerRegistered = false;
    private startupFinished = false;
    private pendingRedirect: ((url: string) => void) | null = null;
    private launchExchange: Promise<void> | null = null;
    private readonly handledCodes = new Set<string>();
    private readonly samlExchanges = new Map<string, Observable<AuthData>>();

    constructor(
        private readonly storageService: StorageService,
        private readonly apiService: ApiService,
    ) { }

    public retrievePassword(email): Observable<boolean> {
        return this.apiService
            .post('/api/forgottenpassword', {email}, ApiFormat.JSON, false)
            .pipe(map(res => true, err => false));
    }

    public revokeTokens(){
        return this.apiService.get('/api/revoke-tokens').pipe(map(response => true));
    }

    public getAPICredentials(): Observable<boolean> {
        return this.apiService
            .get('/api/getcredentials', ApiFormat.JSON, false)
            .pipe(map(
                (credentials: Credentials) => {
                    this.clientId = credentials.clientId;
                    this.clientSecret = credentials.clientSecret;
                    return false;
                }
            ));
    }

    public login(credentials: {email: string, password: string}): Observable<AuthData> {
        const body = {
            grant_type: 'password',
            client_id: this.clientId,
            client_secret: this.clientSecret,
            username: credentials.email,
            password: credentials.password
        };

        return this.apiService
            .post<AuthData>('/oauth/v2/token' , body, ApiFormat.JSON, false)
            .pipe(map((response: any) => {
                const authData = new AuthData('-', '-', response.access_token);
                this.apiService.saveToken(response.access_token, response.user_id, credentials.email);
                return authData;
            }));
    }

    public async checkLicenseAndCossAccess(): Promise<boolean> {
        try {
            const [checkLicense, checkCoss] = await Promise.all([
                this.checkLicense(),
                this.checkCossAccess(),
            ]);

            if (checkLicense === 'expired' && checkCoss) {
                this.limitedAccess = true;
            }

            return checkLicense !== 'false' && checkCoss;
        } catch (err) {
            return false;
        }
    }

    public async checkCossAccess(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.apiService
                .post<{ allowed: boolean }>('/api/check/coss', {source: 'coss'}, ApiFormat.JSON)
                .pipe(map(res => {
                    return res?.allowed || false;
                }))
                .subscribe({
                    next: resolve,
                    error: () => resolve(false),
                });
        });
    }

    public async checkLicense(): Promise<"true"|"false"|"expired"> {
        return new Promise<"true"|"false"|"expired">((resolve) => {
            this.apiService
                .post<{ access: "true"|"false"|"expired" }>('/api/check/licence/access', {source: 'coss'}, ApiFormat.JSON)
                .pipe(map(res => {
                    return res?.access;
                }))
                .subscribe({
                    next: resolve,
                    error: () => resolve("false"),
                });
        });
    }

    public samlPlatform(): 'mobile_app' | 'browser' {
        return Capacitor.isNativePlatform() ? 'mobile_app' : 'browser';
    }

    public checkSSO(matricule: string): Observable<{status: boolean | string, link: string}> {
        return this.apiService.post<{status: boolean | string, link: string}>('/api/check/saml', {
            source: 'app',
            matricule,
            platform: this.samlPlatform(),
        });
    }

    /**
     * Web keeps a full-page redirect. The IdP returns to this origin with `code`,
     * which the login page exchanges.
     */
    public openWebSso(link: string): void {
        void Browser.open({url: link, windowName: '_self'});
    }

    /**
     * Opens the IdP in the system auth session (ASWebAuthenticationSession on iOS,
     * Custom Tabs on Android). The session closes on `com.fivefeedback.coss://saml`.
     * A cold start is delivered through `appUrlOpen` / `getLaunchUrl` as well.
     */
    public openSystemAuthSession(authEndpoint: string): Promise<string> {
        this.listenForSamlCallback();
        return new Promise((resolve, reject) => {
            let settled = false;
            const finish = (error: unknown, url?: string) => {
                if (settled) {
                    return;
                }
                settled = true;
                this.pendingRedirect = null;
                if (error || !url) {
                    reject(error ?? new Error('SAML callback is missing a code'));
                    return;
                }
                const code = readSamlCode(url);
                if (code) {
                    this.claimCode(code);
                    void this.closeAuthSession();
                    resolve(url);
                    return;
                }
                const samlError = readSamlError(url);
                void this.closeAuthSession();
                reject(new Error(samlError || 'SAML callback is missing a code'));
            };

            this.pendingRedirect = (url: string) => finish(null, url);
            InAppBrowser.openSecureWindow({
                authEndpoint,
                redirectUri: SAML_REDIRECT_URI,
            }).then(result => {
                if (result?.redirectedUri) {
                    finish(null, result.redirectedUri);
                    return;
                }
                finish(new Error('SAML callback is missing a code'));
            }).catch(error => finish(error));
        });
    }

    public exchangeSamlCode(code: string, email = ''): Observable<AuthData> {
        const existing = this.samlExchanges.get(code);
        if (existing) {
            return existing;
        }

        const request = this.apiService
            .post<SamlExchangeResponse>('/api/saml/exchange', {code}, ApiFormat.JSON, false)
            .pipe(
                map(response => this.storeSamlToken(response, email)),
                shareReplay({bufferSize: 1, refCount: false}),
            );
        this.samlExchanges.set(code, request);
        return request;
    }

    public listenForSamlCallback(): void {
        if (this.samlListenerRegistered || !Capacitor.isNativePlatform()) {
            return;
        }
        this.samlListenerRegistered = true;
        void App.addListener('appUrlOpen', event => {
            if (!isSamlCallbackUrl(event.url)) {
                return;
            }
            void this.closeAuthSession();
            if (this.pendingRedirect) {
                this.pendingRedirect(event.url);
                return;
            }
            this.acceptUnsolicitedCallback(event.url);
        });
    }

    public async consumeLaunchSamlCode(): Promise<void> {
        try {
            const launch = await App.getLaunchUrl();
            if (launch?.url) {
                this.acceptUnsolicitedCallback(launch.url);
            }
            if (this.launchExchange) {
                await this.launchExchange;
            }
        } catch (error) {
            console.error('Unable to read the SAML launch URL', error);
        } finally {
            this.startupFinished = true;
        }
    }

    public async isLogged(): Promise<boolean> {
        const credentials = await this.storageService.get('user_credentials');
        if (!credentials) {
            this.authStatus.next(new AuthStatus(null, false));
            return false;
        }
        this.apiService.saveToken(credentials.token, credentials.userId, credentials.userEmail);

        return true;
    }

    public logout(): Promise<boolean> {
        this.authStatus.next(new AuthStatus(null, false, false));

        // Promise all is used to wait for all promises to be resolved
        return Promise.all([
            this.storageService.remove('user_credentials'),
            this.storageService.remove('session_data'),
            this.storageService.remove('contacts'),
        ]).then(
            (data) => true,
            (error) => false
        );
    }

    private storeSamlToken(response: SamlExchangeResponse, email: string): AuthData {
        if (!response?.access_token || response.token_type !== 'Bearer' || response.user_id == null) {
            throw Object.assign(new Error('SAML exchange returned an unexpected token'), {status: 502});
        }
        const authData = new AuthData('-', '-', response.access_token);
        this.apiService.saveToken(response.access_token, response.user_id, email);
        return authData;
    }

    private acceptUnsolicitedCallback(url: string): void {
        const code = readSamlCode(url);
        if (!code) {
            const samlError = readSamlError(url);
            if (samlError) {
                void this.closeAuthSession();
                this.samlFailure.next(failureFromSamlCallback(samlError));
            }
            return;
        }
        if (!this.claimCode(code)) {
            return;
        }
        void this.closeAuthSession();
        if (!this.startupFinished) {
            this.launchExchange = this.launchExchange ?? this.trackExchange(code);
            return;
        }
        this.samlCallbackCode.next(code);
    }

    private trackExchange(code: string): Promise<void> {
        return firstValueFrom(this.exchangeSamlCode(code)).then(() => undefined, (error: unknown) => {
            this.samlFailure.next(classifySamlError(error));
        });
    }

    private claimCode(code: string): boolean {
        if (this.handledCodes.has(code)) {
            return false;
        }
        this.handledCodes.add(code);
        return true;
    }

    private async closeAuthSession(): Promise<void> {
        try {
            await InAppBrowser.close();
        } catch {
            // The system session dismisses itself when the redirect matches.
        }
    }
}
